import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { AutocompleteMultiple } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { debounce, identity, uniqBy } from "lodash-es";
import React from "react";
import {
  QueryPimSearchSuppliersSortBy,
  SortDirection,
} from "../../../clients/graphqlTypes.js";
import {
  SearchSuppliers__SupplierFragment,
  useSupplierSelect__PimSearchSuppliersLazyQuery,
} from "./SupplierSelect.generated.js";

const TYPE_TO_SEE_MORE_ID = "__type_to_see_more__" as const;

interface Props {
  label?: string;
  placeholder?: string;
  value: SearchSuppliers__SupplierFragment[];
  required?: boolean;
  disabled?: boolean;
  error?: string;
  onChange: (suppliers: SearchSuppliers__SupplierFragment[]) => void;
}

/**
 * Multi-select variant of {@link SupplierSelect}. Renders the chosen suppliers
 * as removable chips and emits the full list on every change so the add-supplier
 * modal can batch-subscribe to all of them at once.
 */
export const SupplierSelectMultiple = ({
  label,
  placeholder,
  value,
  required = false,
  disabled = false,
  error,
  onChange,
}: Props) => {
  const { t } = useTranslate(["Product", "Global"]);
  const [inputValue, setInputValue] = React.useState("");
  const { supplierOrganisations, totalCount, debouncedRefetch, loading } =
    useSupplierOrganisationsSearch(inputValue);

  // Keep already-selected suppliers in the option set so their chips keep a
  // label even after they scroll out of the latest search results.
  const options = uniqBy(
    [...value, ...supplierOrganisations],
    supplier => supplier.id
  );

  return (
    <AutocompleteMultiple
      placeholder={placeholder ?? t("Type to search", { ns: "Global" })}
      inputLabel={label ?? t("Supplier", { ns: "Product" })}
      inputValue={inputValue}
      options={[
        ...options,
        ...(totalCount > supplierOrganisations.length
          ? [
              {
                title: t("Type to see more...", { ns: "Global" }),
                id: TYPE_TO_SEE_MORE_ID,
              },
            ]
          : []),
      ]}
      getOptionLabel={option => option.title}
      getOptionDisabled={option => option.id === TYPE_TO_SEE_MORE_ID}
      onInputChange={(_, newValue, reason) => {
        if (reason === "input") {
          setInputValue(newValue);
          debouncedRefetch(newValue);
        } else if (reason === "clear") {
          // The built-in clear (X) controls the input too; reset it and the
          // results, matching onBlur/onChange.
          setInputValue("");
          debouncedRefetch("");
        }
      }}
      onBlur={() => {
        // Mirror onChange so a re-focus starts from the empty query instead of
        // the previous term's stale results.
        if (inputValue) {
          setInputValue("");
          debouncedRefetch("");
        }
      }}
      value={value}
      onChange={newValue => {
        onChange(
          newValue.filter(
            v => v.id !== TYPE_TO_SEE_MORE_ID
          ) as SearchSuppliers__SupplierFragment[]
        );
        setInputValue("");
        debouncedRefetch("");
      }}
      loading={loading}
      // Only mark the (empty) search input as required while nothing is
      // selected — otherwise native HTML5 validation blocks submit even though
      // chips are present (the chips, not the input text, carry the value).
      required={required && value.length === 0}
      disabled={disabled}
      error={error}
      filterOptions={identity}
    />
  );
};

const LIMIT = 10;

const SUPPLIER_SORTING: {
  column: QueryPimSearchSuppliersSortBy;
  direction: SortDirection;
}[] = [
  {
    column: "name",
    direction: "asc",
  },
];

function useSupplierOrganisationsSearch(inputValue: string) {
  const client = useApolloClient();
  const [fetchQuery, query] = useSupplierSelect__PimSearchSuppliersLazyQuery({
    client,
    variables: {
      isSubscribed: false,
      limit: LIMIT,
      sorting: SUPPLIER_SORTING,
    },
    fetchPolicy: "network-only",
  });

  const inputValueRef = React.useRef(inputValue);
  inputValueRef.current = inputValue;

  const refetch = React.useCallback(
    async (value: string) => {
      await fetchQuery({
        variables: {
          limit: LIMIT,
          sorting: SUPPLIER_SORTING,
          isSubscribed: false,
          search: value,
        },
      });
    },
    [fetchQuery]
  );

  const debouncedRefetch = React.useMemo(
    () => debounce(refetch, 500),
    [refetch]
  );

  React.useEffect(() => {
    fetchQuery({
      variables: {
        limit: LIMIT,
        sorting: SUPPLIER_SORTING,
        isSubscribed: false,
        search: inputValueRef.current,
      },
    });
  }, [fetchQuery]);

  const supplierOrganisations =
    getDataOrNull((query.data ?? query.previousData)?.pimSearchSuppliers)
      ?.suppliers ?? [];
  const totalCount =
    getDataOrNull((query.data ?? query.previousData)?.pimSearchSuppliers)
      ?.totalCount ?? 0;

  return {
    supplierOrganisations,
    totalCount,
    debouncedRefetch,
    loading:
      query.loading ||
      Boolean(
        inputValue &&
        query?.variables?.search &&
        query?.variables?.search !== inputValue
      ),
  };
}
